import { faDownload, faExternalLink } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { type ComponentPropsWithRef, type ComponentType, type FC } from 'react';
import type { ActionMode } from 'src/components/action-mode';
import { useActionMode } from 'src/components/action-mode';
import type { ActionGroupMode } from 'src/components/buttons-and-actions/action-group';
import { useActionGroupMode } from 'src/components/buttons-and-actions/action-group';
import type { IconProps } from 'src/components/media-and-icons/icon';
import {
  getDefaultLinkStyles,
  getDefaultUnderlinedLinkStyles,
  getNavigationItemStyles,
} from 'src/components/navigation/link';
import { cn } from 'src/utils/cn';

export type Size = 'md' | 'sm';

type BaseButtonProps = {
  icon?: ComponentType<IconProps>;
};

export type ModeButtonProps = BaseButtonProps &
  (
    | {
        mode: 'icon' | 'icon-inverse' | 'dropdown-item' | 'link';
        /** This mode doesn't use `icon`, but you can pass it, in case mode is switched on dynamically. */
        icon?: ComponentType<IconProps>;
      }
    | {
        mode?: 'tab';
        isActive?: boolean;
      }
    | {
        mode: 'segmented-control';
        size?: Size;
        isSelected?: boolean;
      }
    | {
        mode: 'link-underlined';
        behaviour?: 'external' | 'download';
      }
    | {
        mode: 'navigation-item'; // used in the app shell navigation
        icon?: ComponentType<IconProps>;
        isActive?: boolean;
        hideIcon?: undefined;
      }
    | {
        mode?: 'primary';
        size?: Size;
      }
    | {
        mode: 'secondary';
        size?: Size;
      }
  );

export type ButtonProps = {
  /**
   * Configure your button type.
   *
   * @default 'button'
   */
  type?: string;
} & ModeButtonProps &
  ComponentPropsWithRef<'button'>;

// helper type to allow easier access to the custom props
type CustomButtonProps = {
  mode: ModeButtonProps['mode'];
  behaviour?: 'external' | 'download';
  size?: Size;
  isActive?: boolean;
  isSelected?: boolean;
};

/**
 * Your basic button component. Use this for `onClick` interactions.
 */
export const Button: FC<ButtonProps> = ({ ref, ...baseProps }) => {
  const actionMode = useActionMode();
  const actionGroupMode = useActionGroupMode();

  // descruct all custom props
  const {
    mode,
    icon: IconComponent,
    behaviour,
    size = 'md',
    isActive,
    isSelected,
    ...props
  } = {
    type: 'button',
    ...mapActionModeToButton(actionMode),
    ...baseProps,
  } as ButtonProps & CustomButtonProps;

  switch (mode) {
    case 'link-underlined': {
      return (
        <span>
          <button
            {...props}
            className={getDefaultUnderlinedLinkStyles({
              className: props.className,
            })}
            ref={ref}
          />
          {behaviour && (
            <>
              {' '}
              <FontAwesomeIcon
                className={cn(
                  'text-foreground-primary-inverted',
                  props.disabled &&
                    `
                      cursor-not-allowed text-foreground-secondary-disabled
                      hover:text-foreground-secondary-disabled
                    `
                )}
                icon={behaviour === 'download' ? faDownload : faExternalLink}
              />
            </>
          )}
        </span>
      );
    }
    case 'link':
      return (
        <button
          {...props}
          className={getDefaultLinkStyles({
            className: cn('text-left', props.className),
          })}
          ref={ref}
        />
      );
    case 'tab':
      return (
        <button
          {...props}
          className={getTabClasses({
            isActive,
            className: cn('', props.className),
          })}
          ref={ref}
          disabled={props.disabled ?? isActive}
        />
      );
    case 'segmented-control':
      return (
        <button
          {...props}
          className={getButtonSecondaryClasses({
            isSelected,
            actionGroupMode,
            size: 'sm',
            className: props.className,
          })}
          ref={ref}
          disabled={props.disabled ?? isSelected}
        />
      );
    case 'navigation-item':
      return (
        <button
          {...props}
          className={cn(getNavigationItemStyles({ isActive }), props.className)}
          data-slot="sidebar-navigation-item"
          data-active={isActive ? true : undefined}
          ref={ref}
        >
          {IconComponent && <IconComponent size="sm" className="shrink-0" />}

          <div
            className={cn(
              `
                grow truncate
                group-data-[sidebar-expanded=true]:group-data-[sidebar-transitioning=false]:truncate-none
              `,
              // Allow the button to have adjacent elements like a chevron icon
              // for the navigation child group items or a dropdown ellipsis icon
              'flex justify-between'
            )}
          >
            {props.children}
          </div>
        </button>
      );
    case 'icon':
      return (
        <button
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-none bg-transparent text-label-base
              text-foreground-secondary-default
              not-disabled:not-active:hover:text-foreground-secondary-hover
              focus-visible:spaced-focus-style
              active:text-foreground-secondary-active
              disabled:text-foreground-secondary-disabled
            `,
            `
              aria-pressed:bg-background-surface-selected
              aria-pressed:text-foreground-surface-selected!
              aria-pressed:hover:bg-background-surface-selected
            `,
            props.className
          )}
          ref={ref}
        />
      );
    case 'icon-inverse':
      return (
        <button
          {...props}
          className={cn(
            getBaseActionClasses({ isActive }),
            `
              border-none bg-transparent text-label-base
              text-foreground-primary-default
              not-disabled:not-active:hover:text-background-secondary-default
              active:text-foreground-secondary-active
              disabled:text-foreground-secondary-disabled
            `,
            props.className
          )}
          ref={ref}
        />
      );
    case 'dropdown-item':
      return (
        <button
          {...props}
          className={getDropdownItemClasses({
            className: props.className,
            isActive,
          })}
          ref={ref}
        />
      );
    case 'secondary':
      return (
        <button
          {...props}
          className={getButtonSecondaryClasses({
            isSelected: false,
            actionGroupMode,
            size,
            className: props.className,
          })}
          ref={ref}
          disabled={props.disabled ?? isActive}
        />
      );
    case 'primary':
    case undefined:
      return (
        <button
          {...props}
          className={getButtonPrimaryClasses({
            actionGroupMode,
            size,
            className: props.className,
          })}
          ref={ref}
        />
      );
  }
};

export function getTabClasses({
  isActive,
  className,
}: {
  isActive?: boolean;
  className?: string;
}) {
  return cn(
    getBaseActionClasses({ isActive }),
    `
      relative -mb-px px-md pt-sm pb-lg text-body-base
      text-foreground-surface-subdued no-underline
      after:absolute after:bottom-0 after:left-0 after:w-full after:border-b-2
      after:border-b-transparent
      not-disabled:not-active:hover:after:border-b-foreground-surface-subdued
      active:text-foreground-default
      active:after:border-b-border-surface-default
    `,
    isActive &&
      `
        font-body-semibold text-foreground-default
        after:border-b-border-surface-default
        not-disabled:not-active:hover:after:border-b-border-surface-default
      `,
    className
  );
}

export function getDropdownItemClasses({
  className,
  isActive,
}: {
  className?: string;
  isActive?: boolean;
}) {
  return cn(
    getBaseActionClasses({ isActive }),
    `
      inline-block w-full rounded-xs border-none bg-background-surface-default
      px-lg py-sm text-left text-label-base text-foreground-default
      not-disabled:not-active:hover:bg-background-surface-hover
      active:bg-background-surface-selected
      active:text-foreground-surface-selected
      disabled:bg-background-surface-disabled
      disabled:text-foreground-surface-disabled
    `,
    `
      focus-visible:active:contrast-focus-style
      aria-pressed:bg-background-surface-selected!
      aria-pressed:text-foreground-surface-selected!
      aria-pressed:hover:bg-background-surface-selected
    `,
    isActive &&
      `
        bg-background-surface-selected text-foreground-surface-selected
        hover:bg-background-surface-selected!
        focus-visible:contrast-focus-style
      `,
    className
  );
}

export function getButtonSecondaryClasses({
  isSelected,
  actionGroupMode,
  size,
  className,
}: {
  isSelected?: boolean;
  actionGroupMode: ActionGroupMode;
  size?: Size;
  className?: string;
}) {
  return cn(
    getBaseActionClasses({ isActive: isSelected }),
    getActionGroupModeClasses({ actionGroupMode, size }),
    `
      border-none bg-background-secondary-default text-center
      font-label-semibold text-foreground-secondary-default
      not-disabled:not-active:hover:bg-background-secondary-hover
      not-disabled:not-active:hover:text-foreground-secondary-hover
      focus-visible:contrast-focus-style
      active:bg-background-secondary-active
      active:text-foreground-secondary-active
      disabled:bg-background-secondary-disabled
      disabled:text-foreground-secondary-disabled
    `,
    isSelected &&
      `
        active:bg-background-secondary-selected
        active:text-foreground-secondary-selected
        disabled:bg-background-secondary-selected
        disabled:text-foreground-secondary-selected
      `,
    `
      aria-pressed:bg-background-secondary-selected
      aria-pressed:text-foreground-secondary-selected!
      aria-pressed:hover:bg-background-secondary-selected!
    `,
    getActionSizeClasses({ size }),
    className
  );
}

export function getButtonPrimaryClasses({
  actionGroupMode,
  size,
  className,
}: {
  actionGroupMode: ActionGroupMode;
  size?: Size;
  className?: string;
}) {
  return cn(
    getBaseActionClasses({ isActive: undefined }),
    getActionGroupModeClasses({ actionGroupMode, size }),
    `
      border-none bg-background-primary-default text-center font-label-semibold
      text-foreground-primary-default
      not-disabled:not-active:hover:bg-background-primary-hover
      not-disabled:not-active:hover:text-foreground-primary-hover
      focus-visible:contrast-focus-style
      active:bg-background-primary-active active:text-foreground-primary-active
      disabled:bg-background-primary-disabled
      disabled:text-foreground-primary-default
    `,
    `
      aria-pressed:bg-background-primary-selected
      aria-pressed:text-foreground-primary-selected!
      aria-pressed:hover:bg-background-primary-selected!
    `,
    getActionSizeClasses({ size }),
    className
  );
}

/**
 * These classes form the base style of buttons and links.
 */
export function getBaseActionClasses({ isActive }: { isActive?: boolean }) {
  return cn(
    `
      cursor-pointer rounded-xs transition-[color,border-color,background-color]
      duration-150
    `,
    !isActive && 'disabled:cursor-not-allowed',
    isActive && 'disabled:cursor-default'
  );
}

export function getActionGroupModeClasses({
  actionGroupMode,
  size,
}: {
  actionGroupMode: ActionGroupMode;
  size?: Size;
}) {
  return cn(
    actionGroupMode === 'left' &&
      size === 'sm' &&
      'rounded-l-3xl rounded-r-none',
    actionGroupMode === 'left' &&
      size !== 'sm' &&
      'rounded-l-3xl rounded-r-none',
    actionGroupMode === 'right' &&
      size === 'sm' &&
      'rounded-l-none rounded-r-3xl',
    actionGroupMode === 'right' &&
      size !== 'sm' &&
      'rounded-l-none rounded-r-3xl',
    actionGroupMode === 'single' && size === 'sm' && 'rounded-3xl',
    actionGroupMode === 'single' && size !== 'sm' && 'rounded-3xl',
    actionGroupMode === 'left' && 'mr-px',
    actionGroupMode === 'middle' && 'mr-px rounded-none'
  );
}

export function getActionSizeClasses({ size }: { size?: Size }) {
  return cn(
    size === 'sm' ? 'px-lg py-xs text-label-sm' : 'px-xl py-md text-label-base'
  );
}

export function mapActionModeToButton(
  actionMode: ActionMode | null
): ModeButtonProps | null {
  if (!actionMode) return null;

  switch (actionMode.mode) {
    case 'button-primary':
      return { ...actionMode, mode: 'primary' };
    case 'button-secondary':
      return { ...actionMode, mode: 'secondary' };
    case 'dropdown':
      return { ...actionMode, mode: 'dropdown-item' };
    case 'tab':
      return { ...actionMode, mode: 'tab' };
    case 'navigation-item':
      return { ...actionMode, mode: 'navigation-item' };
    case 'segmented-control':
      return { ...actionMode, mode: 'segmented-control' };
    case 'sidebar-item':
      throw new Error(
        'Buttons currently do not support action mode="sidebar".'
      );
    case 'navigation-child-item':
      throw new Error(
        'Buttons currently do not support action mode="navigation-child-item".'
      );
  }
}
